import apiClient from './apiService';
import { AxiosResponse } from 'axios';

const apiService = apiClient;

export const LAW_TYPE_OPTIONS = [
  { value: 'TAX', label: 'Tax Laws' },
  { value: 'ENTITY_CONSTITUTION', label: 'Entity Constitution' },
  { value: 'FINANCIAL_REPORTING', label: 'Financial Reporting' },
  { value: 'INDUSTRY_SPECIFIC', label: 'Industry Specific' },
  { value: 'LABOUR', label: 'Labour Laws' },
  { value: 'MINING', label: 'Mining Laws' },
  { value: 'ENVIRONMENT', label: 'Environment / ESG' },
  { value: 'DATA_PROTECTION', label: 'Data Protection' },
  { value: 'OTHER', label: 'Other' }
];

export interface ApplicableLaw {
  id: number;
  code?: string | null;
  law_name: string;
  regulator_name: string;
  major_provisions: string;
  applicability_start_date?: string | null;
  applicability_end_date?: string | null;
  jurisdiction: string;
  law_type: string;
  is_active: boolean;
  created_by: number;
  created_on: string;
  last_modified_by?: number;
  last_modified_on?: string;
  display_order: number;
  // Approval workflow (maker-checker) — added by the workflow-quad migration
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
}

export interface ApplicableLawQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  law_type?: string;
  jurisdiction?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  total?: number;
}

export interface ApplicableLawStats {
  total: number;
  active: number;
  inactive: number;
  by_type: Record<string, number>;
}

export interface AuditRecord {
  audit_id: number;
  entity_id: number;
  law_name?: string;
  jurisdiction?: string;
  law_type?: string;
  action: string;
  old_snapshot?: any;
  new_snapshot?: any;
  changed_fields?: string[];
  changed_by: number;
  changed_by_username: string;
  changed_by_email?: string;
  changed_on: string;
  change_reason?: string;
  admin_comments?: string;
  ip_address?: string;
  user_agent?: string;
}

class ApplicableLawsService {
  async getAll(params?: ApplicableLawQueryParams): Promise<ApplicableLaw[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status && params.status !== 'ALL') {
        queryParams.append('status', params.status);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.law_type) {
        queryParams.append('law_type', params.law_type);
      }
      if (params?.jurisdiction) {
        queryParams.append('jurisdiction', params.jurisdiction);
      }

      const queryString = queryParams.toString();
      const url = `/api/v1/gov/applicable-laws${queryString ? `?${queryString}` : ''}`;
      const response: AxiosResponse<ApiResponse<ApplicableLaw[]>> = await apiService.get(url);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to fetch applicable laws');
    } catch (error: any) {
      console.error('Error fetching applicable laws:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Approved + active applicable laws for pickers. Hits the unified /dropdown
   * endpoint (approval_filter = approved) so draft/submitted/rejected laws never
   * leak into a select. Returns the minimal {id, law_name, code} shape the
   * obligation form needs, typed as ApplicableLaw for the existing state.
   */
  async getDropdown(): Promise<ApplicableLaw[]> {
    const response = await apiService.get('/api/v1/gov/applicable-laws/dropdown');
    const items = (response.data?.items ?? []) as Array<{ id: number; name: string; code?: string }>;
    return items.map((it) => ({
      id: it.id,
      law_name: it.name,
      code: it.code ?? null,
    })) as ApplicableLaw[];
  }

  async getById(lawId: number): Promise<ApplicableLaw> {
    try {
      const response: AxiosResponse<ApiResponse<ApplicableLaw>> = await apiService.get(`/api/v1/gov/applicable-laws/${lawId}`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to fetch applicable law');
    } catch (error: any) {
      console.error(`Error fetching applicable law ${lawId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async create(payload: Partial<ApplicableLaw>): Promise<ApplicableLaw> {
    try {
      const normalizedPayload = {
        ...payload,
        applicability_start_date: payload.applicability_start_date || null,
        applicability_end_date: payload.applicability_end_date || null
      };
      const response: AxiosResponse<ApiResponse<ApplicableLaw>> = await apiService.post('/api/v1/gov/applicable-laws', normalizedPayload);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to create applicable law');
    } catch (error: any) {
      console.error('Error creating applicable law:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async update(lawId: number, payload: Partial<ApplicableLaw>): Promise<ApplicableLaw> {
    try {
      const normalizedPayload = {
        ...payload,
        applicability_start_date: payload.applicability_start_date || null,
        applicability_end_date: payload.applicability_end_date || null
      };
      const response: AxiosResponse<ApiResponse<ApplicableLaw>> = await apiService.put(`/api/v1/gov/applicable-laws/${lawId}`, normalizedPayload);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to update applicable law');
    } catch (error: any) {
      console.error(`Error updating applicable law ${lawId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async delete(lawId: number): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await apiService.delete(`/api/v1/gov/applicable-laws/${lawId}`);
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete applicable law');
      }
    } catch (error: any) {
      console.error(`Error deleting applicable law ${lawId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async toggleStatus(lawId: number): Promise<ApplicableLaw> {
    try {
      const response: AxiosResponse<ApiResponse<ApplicableLaw>> = await apiService.patch(`/api/v1/gov/applicable-laws/${lawId}/toggle`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to toggle applicable law status');
    } catch (error: any) {
      console.error(`Error toggling applicable law ${lawId} status:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async getStatistics(): Promise<ApplicableLawStats> {
    try {
      const response: AxiosResponse<ApiResponse<ApplicableLawStats>> = await apiService.get('/api/v1/gov/applicable-laws/statistics');
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to fetch statistics');
    } catch (error: any) {
      console.error('Error fetching applicable law statistics:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async getHistory(lawId: number): Promise<AuditRecord[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await apiService.get(`/api/v1/gov/applicable-laws/${lawId}/history`);
      if (response.data.success && response.data.data) {
        // Map GRC_master_history rows into the AuditRecord shape the timeline expects.
        return response.data.data.map((r: any) => ({
          audit_id: r.id,
          entity_id: r.entity_id,
          action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
          changed_by: r.changed_by,
          changed_by_username: r.changed_by_username || 'Unknown',
          changed_on: r.changed_on,
          change_reason: r.remarks,
        })) as AuditRecord[];
      }
      throw new Error(response.data.error || 'Failed to fetch audit history');
    } catch (error: any) {
      console.error(`Error fetching applicable law history ${lawId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /** Submit a draft/rejected law for approval. */
  async submit(lawId: number): Promise<void> {
    const resp = await apiService.post(`/api/v1/gov/applicable-laws/${lawId}/submit`);
    if (resp.data && resp.data.success === false) throw new Error(resp.data.error || 'Submit failed');
  }

  /** Approve a submitted law (approver scope only). */
  async approve(lawId: number, note?: string): Promise<void> {
    const resp = await apiService.post(`/api/v1/gov/applicable-laws/${lawId}/approve`, { note });
    if (resp.data && resp.data.success === false) throw new Error(resp.data.error || 'Approve failed');
  }

  /** Reject a submitted law with mandatory reason (approver scope only). */
  async reject(lawId: number, reason: string): Promise<void> {
    const resp = await apiService.post(`/api/v1/gov/applicable-laws/${lawId}/reject`, { reason });
    if (resp.data && resp.data.success === false) throw new Error(resp.data.error || 'Reject failed');
  }

  /** Reopen an approved/rejected law back to draft (approver scope only). */
  async reopen(lawId: number): Promise<void> {
    const resp = await apiService.post(`/api/v1/gov/applicable-laws/${lawId}/reopen`);
    if (resp.data && resp.data.success === false) throw new Error(resp.data.error || 'Reopen failed');
  }

  validateData(data: Partial<ApplicableLaw>, isUpdate = false): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    if (!isUpdate) {
      if (!data.law_name || !data.law_name.trim()) {
        errors.push('Law name is required');
      }
      if (!data.regulator_name || !data.regulator_name.trim()) {
        errors.push('Regulator name is required');
      }
      if (!data.major_provisions || !data.major_provisions.trim()) {
        errors.push('Major provisions are required');
      }
      if (!data.jurisdiction || !data.jurisdiction.trim()) {
        errors.push('Jurisdiction is required');
      }
      if (!data.law_type) {
        errors.push('Law type is required');
      }
    }

    if (data.applicability_start_date && data.applicability_end_date) {
      const start = new Date(data.applicability_start_date);
      const end = new Date(data.applicability_end_date);
      if (end < start) {
        errors.push('End date cannot be before start date');
      }
    }

    if (data.law_type && !LAW_TYPE_OPTIONS.some(option => option.value === data.law_type)) {
      errors.push('Invalid law type');
    }

    return { valid: errors.length === 0, errors };
  }
}

const applicableLawsService = new ApplicableLawsService();
export default applicableLawsService;

// ===========================================================================
// Law Provisions — child rows under an applicable law
// ===========================================================================

export interface LawProvision {
  id: number;
  code?: string | null;
  entity_id: number;
  law_id: number;
  section_reference?: string | null;
  title: string;
  description?: string | null;
  penalty_description?: string | null;
  display_order?: number;
  is_active: number;
  is_approved: number;
}

class LawProvisionsService {
  async listForLaw(lawId: number): Promise<LawProvision[]> {
    const res: AxiosResponse<ApiResponse<LawProvision[]>> = await apiService.get(`/api/v1/gov/applicable-laws/${lawId}/provisions`);
    if (res.data.success && res.data.data) return res.data.data;
    return [];
  }

  async create(payload: Partial<LawProvision>): Promise<LawProvision> {
    const res: AxiosResponse<ApiResponse<LawProvision>> = await apiService.post('/api/v1/gov/law-provisions', payload);
    if (res.data.success && res.data.data) return res.data.data;
    throw new Error(res.data.error || 'Failed to create provision');
  }

  async update(id: number, payload: Partial<LawProvision>): Promise<LawProvision> {
    const res: AxiosResponse<ApiResponse<LawProvision>> = await apiService.put(`/api/v1/gov/law-provisions/${id}`, payload);
    if (res.data.success && res.data.data) return res.data.data;
    throw new Error(res.data.error || 'Failed to update provision');
  }

  async delete(id: number): Promise<void> {
    const res: AxiosResponse<ApiResponse<void>> = await apiService.delete(`/api/v1/gov/law-provisions/${id}`);
    if (!res.data.success) throw new Error(res.data.error || 'Failed to delete provision');
  }
}

export const lawProvisionsService = new LawProvisionsService();
